import React, { useMemo, useState } from "react";
import { Code2, Database, FolderTree, Search, Terminal, Workflow } from "lucide-react";

import { PublicLayout } from "@/components/layout/PublicLayout";
import { useTranslation } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const AGENT_DOC_SECTIONS = [
  {
    title: "Tổng quan",
    icon: Code2,
    content:
      "Agents là trung tâm điều phối của Cloudface, cho phép cấu hình và vận hành các AI worker chuyên biệt. Mỗi agent là một cấu hình độc lập, liên kết giữa model ngôn ngữ, system prompt và danh sách tool được phép dùng.",
  },
  {
    title: "Vòng đời kỹ thuật",
    icon: Workflow,
    content:
      "Agent đi qua các giai đoạn: Draft -> Verification -> Deployment. Trong lúc thực thi, hệ thống sẽ hydrate context và variables vào system prompt trước khi gọi nhà cung cấp model.",
  },
  {
    title: "Lưu trữ và thực thể",
    icon: Database,
    content:
      "Trạng thái được quản lý theo từng thực thể nghiệp vụ. Store tài khoản quản lý metadata cấp người dùng, còn conversation state giữ lịch sử và ngữ cảnh theo agent instance.",
  },
  {
    title: "Luồng thực thi",
    icon: Terminal,
    content:
      "1. Thay biến trong prompt. 2. Kiểm tra tool khả dụng. 3. Gọi model. 4. Che dữ liệu nhạy cảm nếu cần. 5. Ghi audit log. 6. Trả kết quả về giao diện.",
  },
];

export default function AgentsDocPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const filtered = useMemo(
    () =>
      AGENT_DOC_SECTIONS.filter(
        (section) => section.title.toLowerCase().includes(search.toLowerCase()) || section.content.toLowerCase().includes(search.toLowerCase()),
      ),
    [search],
  );

  return (
    <PublicLayout>
      <div className="mx-auto max-w-4xl space-y-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold tracking-tight">Đặc tả kỹ thuật Agents</h1>
            <p className="text-muted-foreground">Tài liệu kiến trúc và ghi chú triển khai cho lớp vận hành agent AI.</p>
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Tìm trong tài liệu..." className="pl-10" value={search} onChange={(event) => setSearch(event.target.value)} aria-label="Tìm kiếm tài liệu kỹ thuật" />
          </div>
        </div>

        <div className="grid gap-6">
          {filtered.map((section) => (
            <Card key={section.title} className="overflow-hidden border-white/5 bg-card/40 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center gap-3 border-b border-white/5 bg-muted/20">
                <div className="rounded-lg bg-primary/10 p-2 text-primary">
                  <section.icon className="h-5 w-5" />
                </div>
                <CardTitle className="text-xl">{section.title}</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="whitespace-pre-wrap leading-relaxed text-muted-foreground">{section.content}</p>
              </CardContent>
            </Card>
          ))}

          <Card className="border-white/5 bg-slate-950 font-mono text-xs text-slate-300">
            <CardHeader className="border-b border-white/10 px-6 py-4">
              <div className="flex items-center gap-2">
                <FolderTree className="h-4 w-4" />
                <span>Cấu trúc dự án</span>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <pre>{`src/
├── components/agents/   # Card, modal, quick test
├── lib/agents-store.ts  # Zustand state management
├── pages/AgentsPage.tsx # Màn hình quản lý agent
└── lib/security.ts      # Masking và bảo vệ dữ liệu`}</pre>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-center gap-4 py-8">
          <Badge variant="outline" className="px-4 py-1">
            v2.4.0 Sẵn sàng triển khai
          </Badge>
          <Badge variant="outline" className="px-4 py-1">
            Kiến trúc doanh nghiệp
          </Badge>
        </div>
      </div>
    </PublicLayout>
  );
}
